/**
 * Cortex - Main App Component
 * Routes: Scorecard (S2) + Definitions (S4), behind the carried auth shell.
 * Adapted from fast-cortex-orderbook (job-costing routes not carried).
 */
import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider } from './contexts/AuthContext'
import { DateRangeProvider } from './contexts/DateRangeContext'
import { GlobalFilterProvider } from './contexts/GlobalFilterContext'
import { ThemeProvider } from './contexts/ThemeContext'
import ProtectedRoute from './components/ProtectedRoute'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import Login from './pages/Login'
import Scorecard from './pages/Scorecard'
import MetricDetail from './pages/MetricDetail'
import Margin from './pages/Margin'
import Capacity from './pages/Capacity'
import OrderBook from './pages/OrderBook'
import Delivery from './pages/Delivery'
import Reconciliation from './pages/Reconciliation'
import Intelligence from './pages/Intelligence'
import Analytics from './pages/Analytics'
import Definitions from './pages/Definitions'

// Serve under Vite's base path (BASE_URL). Default build is '/', so basename is '/'
// (a no-op); the container build uses base=/app/ to sit the SPA under /app.
const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, '') || '/'

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
})

// Shared shell: auth gate + layout + per-page error boundary
const Screen = ({ children }) => (
  <ProtectedRoute>
    <Layout>
      <ErrorBoundary>{children}</ErrorBoundary>
    </Layout>
  </ProtectedRoute>
)

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <Router basename={BASENAME}>
          <AuthProvider>
            <DateRangeProvider>
              <GlobalFilterProvider>
                <ThemeProvider>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    {/* Overview (the reframed scorecard) is the front door */}
                    <Route path="/" element={<Screen><Scorecard /></Screen>} />
                    {/* Analytics v2 — the explain/decide layer, beside Overview (A3) */}
                    <Route path="/analytics" element={<Screen><Analytics /></Screen>} />
                    <Route path="/metric/:metricId" element={<Screen><MetricDetail /></Screen>} />
                    <Route path="/margin" element={<Screen><Margin /></Screen>} />
                    <Route path="/capacity" element={<Screen><Capacity /></Screen>} />
                    <Route path="/order-book" element={<Screen><OrderBook /></Screen>} />
                    <Route path="/delivery" element={<Screen><Delivery /></Screen>} />
                    <Route path="/reconciliation" element={<Screen><Reconciliation /></Screen>} />
                    <Route path="/governance" element={<Navigate to="/governance/definitions" replace />} />
                    <Route path="/intelligence" element={<Screen><Intelligence /></Screen>} />
                    <Route path="/governance/definitions" element={<Screen><Definitions /></Screen>} />
                    {/* legacy paths */}
                    <Route path="/definitions" element={<Navigate to="/governance/definitions" replace />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </ThemeProvider>
              </GlobalFilterProvider>
            </DateRangeProvider>
          </AuthProvider>
        </Router>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
